/**
 * Revenue (`/revenue`, TMMRSL-0031 / TMMRSL-0058): `GET /revenue?from=&to=` — the totals per
 * currency (retail = gross collected, wholesale, your margin, affiliate cost, your net = margin
 * less affiliate commission, tax). Every table renders through `PerCurrency`: nothing is ever
 * added across currencies.
 */
import { useCallback, useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { resellerAdminApi, type ResellerRevenue, type RevenueTenantRow } from '@/api/resellerAdmin';
import { ApiErrorState } from '@/components/ApiErrorState';
import { PageHeader } from '@/components/PageHeader';
import { PerCurrency, type MoneyColumn } from '@/components/PerCurrency';
import { PeriodPicker } from '@/components/PeriodPicker';
import { ReportScope } from '@/components/ReportScope';
import { useRemote } from '@/components/useRemote';
import type { PeriodRange } from '@/i18n/periods';

type Amounts = Omit<RevenueTenantRow, 'slug' | 'name'>;

function revenueColumns<T extends Amounts>(t: TFunction): MoneyColumn<T>[] {
  return [
    { key: 'retail', label: t('money.retail'), minor: (row) => row.retailMinor },
    { key: 'wholesale', label: t('money.wholesale'), minor: (row) => row.wholesaleMinor },
    { key: 'margin', label: t('money.margin'), minor: (row) => row.resellerMarginMinor },
    { key: 'affiliate', label: t('money.affiliateCost'), minor: (row) => row.affiliateCostMinor },
    // "Your net": the margin less affiliate commission (tmm-api — there is no revenue share).
    { key: 'share', label: t('money.resellerShare'), minor: (row) => row.resellerShareMinor },
    { key: 'tax', label: t('money.tax'), minor: (row) => row.taxMinor },
  ];
}

export function RevenueScreen() {
  const [range, setRange] = useState<PeriodRange | null>(null);

  return (
    <Box>
      <PageHeader id="revenue" actions={<PeriodPicker screen="revenue" onChange={setRange} />} />
      {range ? <RevenueBody from={range.from} to={range.to} /> : null}
    </Box>
  );
}

function RevenueBody({ from, to }: { from: string | undefined; to: string | undefined }) {
  const { t } = useTranslation();
  const load = useCallback(() => resellerAdminApi.getRevenue({ from, to }), [from, to]);
  const { status, data, error, reload } = useRemote(load);

  if (status === 'error') return <ApiErrorState what={t('errors.what.revenue')} error={error} onRetry={reload} />;
  if (!data) {
    return (
      <Box role="status" aria-label={t('common.loading')}>
        <Skeleton variant="rounded" height={220} />
      </Box>
    );
  }
  return <RevenueReady revenue={data} />;
}

function RevenueReady({ revenue }: { revenue: ResellerRevenue }) {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <ReportScope from={revenue.from} to={revenue.to} />
      <Box>
        <Typography variant="subtitle1" component="h2">
          {t('revenue.totalsTitle')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
          {t('money.perCurrencyNote')}
        </Typography>
        <PerCurrency
          rows={revenue.totals}
          columns={revenueColumns(t)}
          label={t('revenue.totalsTitle')}
          empty={t('revenue.empty')}
        />
      </Box>
    </Box>
  );
}
